import { CloudActions } from '@portal/portal-core';
import { call, debounce, put } from 'redux-saga/effects';

import { convertToStringMatchRelaxed } from 'utils';

import { checkCostCenterNameFailure, checkCostCenterNameSuccess } from '../actions';
import type { LicenseSearchResult } from '../actions';
import { SUPER_ADMIN_CHECK_COST_CENTER_NAME } from '../types';
import type { SuperClientConfig, WrapResult } from '../api';
import { sendAction } from '../api';
import { formatSearchResults } from '../utils';

interface CheckCostCenterNameAction {
  type: string;
  name: string;
  costCenterType: string;
}

// Capped small: this only needs enough hits to find an exact (relaxed-string) name match, not a
// browsable page of results — unlike the license search view, this is a yes/no lookup.
const CHECK_LIMIT = 5;

export function* checkCostCenterName(
  config: SuperClientConfig,
  action: CheckCostCenterNameAction,
): Generator<unknown, void, unknown> {
  try {
    const searchAction = CloudActions.elasticsearchCostCenterSearch(
      action.name,
      action.costCenterType,
      undefined,
      CHECK_LIMIT,
      0,
    );
    const res = (yield call(sendAction, config, searchAction)) as WrapResult;
    if (res.succeeded) {
      const hits = (res.data as LicenseSearchResult | undefined)?.hits ?? [];
      // validateDuplicateCostCenterName (modal-utils.ts) does the actual exact-match comparison
      // against this relaxed-string list.
      const relaxedNameMatches = hits
        .map((hit) => convertToStringMatchRelaxed(hit._source.json.name))
        .filter((name): name is string => Boolean(name));
      // Flattened { costCenter, productAccess } rows for every product on every hit — reused by
      // the license modal's duplicate-product check (hasMatchingProductNameAndProductAccessType,
      // modal-utils.ts) instead of a second backend call, since these same 5 capped hits already
      // carry the full productAccess arrays needed to answer "does this cost center name already
      // have this product+type combination".
      const matchingLicenseRows = formatSearchResults(hits);
      yield put(checkCostCenterNameSuccess(action.name, relaxedNameMatches, matchingLicenseRows));
    } else {
      throw new Error('response failed');
    }
  } catch (error) {
    console.error('Check Cost Center Name -- ', error);
    yield put(checkCostCenterNameFailure(action.name, (error as Error).message));
  }
}

/* istanbul ignore next reason: no saga watch tests */
export function* watchCheckCostCenterName(
  config: SuperClientConfig,
): Generator<unknown, void, unknown> {
  yield debounce(400, SUPER_ADMIN_CHECK_COST_CENTER_NAME, checkCostCenterName, config);
}
